'use client';

import React, { useState, useEffect } from 'react';
import { BookOpen, Menu, X } from 'lucide-react';
import { NAV_ITEMS, PRODUCT_LINKS, RESOURCE_LINKS } from '../constants';
import { gridMetrics } from './hero/HeroGrid';
import { routePath } from '@/lib/routes';
import ProductsMenu from './ProductsMenu';
import ResourcesMenu from './ResourcesMenu';
import { LOGIN_URL, REGISTER_URL } from '@/lib/app-url';

/** Minimum number of cells the navbar content occupies — used as a floor on
 *  narrow viewports where the grid container itself is small. */
const MIN_NAV_CELLS = 6;

/** Grid line pattern, painted only in the navbar's side regions so the content
 *  area is never covered by the grid (in either scroll state). Vertical lines
 *  only: inside the one-row navbar the horizontal component could only draw
 *  the doc-top edge line (a floating fragment at the screen edge — the sheet
 *  has no rule at its cut edge; see the matching body::before in globals.css)
 *  and the bottom line, which the body grid / scrolled border already own. */
const GRID_IMAGE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right,  rgba(15,23,42,0.07) 1px, transparent 1px)',
  backgroundSize: 'var(--grid-cell, 72px) var(--grid-cell, 72px)',
};

/** Same pattern in light hairlines — used while the navbar floats over an
 *  opaque drenched surface, matching the light grid those bands repaint. */
const GRID_IMAGE_LIGHT: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right,  rgba(255,255,255,0.09) 1px, transparent 1px)',
  backgroundSize: 'var(--grid-cell, 72px) var(--grid-cell, 72px)',
};

/** Icons shown before specific nav labels ("The Redirect" is the learn/blog
 *  section — the book marks it as reading material at a glance). */
const NAV_ICONS: Record<string, React.ReactNode> = {
  'The Redirect': <BookOpen size={15} strokeWidth={1.8} aria-hidden />,
};

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [overDark, setOverDark] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contentStyle, setContentStyle] = useState<React.CSSProperties>({});
  const [leftStyle, setLeftStyle] = useState<React.CSSProperties>({});
  const [rightStyle, setRightStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Context-aware theme: flip to light-on-dark while an opaque drenched
  // surface (dark/pink RowBand, the closing CTA card, the footer) sits under
  // the navbar — the frosted white bar turns muddy over those. Surfaces mark
  // themselves with data-nav-surface; they re-snap on grid-relayout, so
  // re-check then too. (All such surfaces are md+ / hidden on mobile, where
  // their rects collapse to zero height and the check stays false.)
  useEffect(() => {
    let raf = 0;
    const check = () => {
      raf = 0;
      // Nav is exactly one grid row tall — sample at the middle of that row.
      const midline = gridMetrics(document.documentElement.clientWidth).cell / 2;
      const dark = Array.from(
        document.querySelectorAll<HTMLElement>('[data-nav-surface]'),
      ).some((el) => {
        const r = el.getBoundingClientRect();
        return r.height > 0 && r.top <= midline && r.bottom >= midline;
      });
      setOverDark(dark);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('grid-relayout', schedule);
    // RowBands position themselves in their own effects (possibly after this
    // one) and announce it — without this, a dark band at the very top of a
    // page (dark-hero layout) wouldn't flip the nav until the first scroll.
    window.addEventListener('nav-surface-change', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('grid-relayout', schedule);
      window.removeEventListener('nav-surface-change', schedule);
      cancelAnimationFrame(raf);
    };
  }, []);

  // The content chunk spans the grid container; the grid is painted only in the
  // two side regions (left of the chunk, right of it). So the content area always
  // shows the navbar's own background — slate-50 unscrolled, frosted white
  // scrolled — with NO grid behind it, and only the sides show the grid.
  useEffect(() => {
    const update = () => {
      const w = document.documentElement.clientWidth;
      const { cell, snapped, offsetX } = gridMetrics(w);
      if (snapped < (MIN_NAV_CELLS + 2) * cell) {
        // Fallback for narrow viewports: the chunk fills the whole bar (solid
        // toolbar, no side grid). A centered max-width chunk here would sit
        // misaligned over the page grid with stray line fragments beside it.
        setContentStyle({});
        setLeftStyle({});
        setRightStyle({});
        return;
      }
      // Symmetric content band. Redraw only the LEFT edge divider (the left
      // strip's edge line gets clipped); the right divider is the right strip's
      // first grid line, so drawing it here too would double it.
      setContentStyle({
        width: `${snapped}px`,
        marginLeft: `${offsetX}px`,
        boxShadow: 'inset 1px 0 0 0 rgba(15,23,42,0.07)',
      });
      // Left side region [0, offsetX] and right side region [offsetX+snapped, w],
      // each with the grid aligned to the page grid. (The grid *image* is
      // spread in at render time so it can follow the dark/light theme.)
      setLeftStyle({ width: `${offsetX}px`, backgroundPosition: `${offsetX}px 0` });
      setRightStyle({ width: `${w - offsetX - snapped}px`, backgroundPosition: '0 0' });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled
          ? overDark
            ? 'bg-[#14141c]/85 backdrop-blur-lg shadow-[0_4px_24px_-12px_rgba(0,0,0,0.5)]'
            : 'bg-white/70 backdrop-blur-lg shadow-[0_4px_24px_-12px_rgb(15_23_42/0.12)]'
          : ''
      }`}
      // Exactly one grid row tall, so the navbar IS the top row of the page grid
      // and its bottom edge lands on the first horizontal grid line.
      style={{ height: 'var(--grid-cell, 72px)' }}
    >
      {/* Grid painted ONLY in the side regions — never behind the content. */}
      <div
        aria-hidden
        className="grid-gutter absolute inset-y-0 left-0 pointer-events-none"
        style={{ ...leftStyle, ...(overDark ? GRID_IMAGE_LIGHT : GRID_IMAGE) }}
      />
      <div
        aria-hidden
        className="grid-gutter absolute inset-y-0 right-0 pointer-events-none"
        style={{ ...rightStyle, ...(overDark ? GRID_IMAGE_LIGHT : GRID_IMAGE) }}
      />
      {/* Bottom border only once scrolled — the white nav then covers the page
          grid line, so it needs its own. Unscrolled, the page grid line shows
          through as a single line (no doubled border) and decorations sit on it
          cleanly. */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-px transition-opacity duration-300 ${
          overDark ? 'bg-white/10' : 'bg-[rgba(15,23,42,0.07)]'
        } ${isScrolled ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* Brand accent that strengthens the line once scrolled. */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-px transition-opacity duration-300 bg-gradient-to-r from-transparent via-brand-200 to-transparent ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Content chunk — width = grid_container - 2 cells, centered on the same
          axis as the page content and vertically centered in the row. Bg covers
          only this chunk; the cell on each side stays transparent so the body
          grid lines show there. */}
      <div
        className={`grid-edge relative z-[1] flex justify-between items-center h-full px-8 transition-colors duration-300 ${
          // Unscrolled the chunk is opaque — page color normally, or the
          // band surface color over a dark hero (matches the scrolled
          // homepage reference: no grid behind the nav content, grid only
          // in the side margins).
          !isScrolled ? (overDark ? 'bg-[#14141c]' : 'bg-slate-50') : ''
        }`}
        style={
          overDark && contentStyle.boxShadow
            ? { ...contentStyle, boxShadow: 'inset 1px 0 0 0 rgba(255,255,255,0.09)' }
            : contentStyle
        }
      >
        {/* Logo — dark and light variants stacked, crossfaded with the theme. */}
        <a href={routePath('home')} className="relative flex items-center">
          <img
            src="/logos/logo-dark.png"
            alt="rii.link"
            className={`h-8 w-auto transition-opacity duration-300 ${
              overDark ? 'opacity-0' : 'opacity-100'
            }`}
          />
          <img
            src="/logos/logo-light.png"
            alt=""
            aria-hidden
            className={`absolute left-0 h-8 w-auto transition-opacity duration-300 ${
              overDark ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center space-x-6">
          {NAV_ITEMS.map((item) => {
            if (item.label === 'Products') return <ProductsMenu key={item.label} dark={overDark} />;
            if (item.label === 'Resources') return <ResourcesMenu key={item.label} dark={overDark} />;
            return (
              <a
                key={item.label}
                href={item.href}
                className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  overDark ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-brand-600'
                }`}
              >
                {NAV_ICONS[item.label]}
                {item.label}
              </a>
            );
          })}
        </div>

        {/* CTA Buttons */}
        <div className="hidden md:flex items-center space-x-4">
          <a
            href={LOGIN_URL}
            className={`text-sm font-semibold transition-colors ${
              overDark ? 'text-white hover:text-white/80' : 'text-slate-900 hover:text-brand-600'
            }`}
          >
            Log in
          </a>
          <a
            href={REGISTER_URL}
            className={`px-4 py-2 text-sm font-semibold rounded-full transition-all shadow-lg ${
              overDark
                ? 'text-slate-900 bg-white hover:bg-white/90'
                : 'text-white bg-slate-900 hover:bg-slate-800 hover:shadow-slate-900/20'
            }`}
          >
            Start for free
          </a>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-slate-600 hover:text-slate-900"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-100 absolute w-full max-h-[calc(100vh-var(--grid-cell,72px))] overflow-y-auto">
          <div className="px-4 pt-2 pb-6 space-y-1">
            {NAV_ITEMS.map((item) => {
              // Products / Resources expand into their page lists on mobile (the
              // desktop mega-menu/dropdown aren't available on touch), so the
              // feature and resource pages are reachable from the phone nav.
              const group =
                item.label === 'Products'
                  ? PRODUCT_LINKS
                  : item.label === 'Resources'
                    ? RESOURCE_LINKS
                    : null;

              if (group) {
                return (
                  <div key={item.label} className="pt-1">
                    <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {item.label}
                    </p>
                    {group.map((link) => (
                      <a
                        key={link.href}
                        href={link.href}
                        className="block rounded-md px-3 py-2 pl-6 text-base font-medium text-slate-700 hover:bg-brand-50 hover:text-brand-600"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                );
              }

              return (
                <a
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-2 px-3 py-2 text-base font-medium text-slate-700 hover:text-brand-600 hover:bg-brand-50 rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {NAV_ICONS[item.label]}
                  {item.label}
                </a>
              );
            })}
            <div className="pt-4 flex flex-col space-y-3">
               <a href={LOGIN_URL} className="w-full text-center px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg">
                Log in
              </a>
              <a href={REGISTER_URL} className="w-full text-center px-4 py-2 text-sm font-semibold text-white bg-brand-500 rounded-lg">
                Sign up
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
